import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {UnassignProductUserEntitlementsTool} from 'src/modules/bulk-update';
import {PreparedEntitlementsView, ProductResourceSuggest, useExpandRoles} from 'src/modules/entitlements';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';

import {
    userEntitlementsAccountEntitlements,
    userEntitlementsCustomEntitlements,
    userEntitlementsCustomEntitlementsTools,
    userEntitlementsHeader,
    userEntitlementsLoadingContainer,
    userEntitlementsNoDataLabel,
} from '../user-entitlements.module.styl';

import {McpAccountEntitlementsPagination} from './components/mcp-account-entitlements-pagination';
import {McpAccountResourceNameHeader} from './components/mcp-account-resource-name.header';
import {McpUserEntitlementsPagination} from './components/mcp-user-entitlements-pagination';
import {McpUserResourceNameHeader} from './components/mcp-user-resource-name.header';
import {
    ACCOUNT_SECTION_NAME,
    MCP_ACCOUNT_SELECTION_KEY,
    MCP_USER_SELECTION_KEY,
    NO_ASSIGNED_ACCOUNT_MCP_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_USER_MCP_ENTITLEMENT_DESCRIPTION,
    USER_SECTION_NAME,
} from './constants';
import {useGetProductAccountEntitlements} from './hooks/useGetProductAccountEntitlements';
import {useGetProductUserEntitlements} from './hooks/useGetProductUserEntitlements';
import {mcpAccountEntitlementsStateActions} from './slices/mcp-account-entitlements-state.slice';
import {mcpAccountEntitlementsActions} from './slices/mcp-account-entitlements.slice';
import {mcpUserEntitlementsStateActions} from './slices/mcp-user-entitlements-state.slice';
import {mcpUserEntitlementsActions} from './slices/mcp-user-entitlements.slice';


type Props = {
    accountId: number,
    userId: number,
};

export const McpUserEntitlementsTab = ({accountId, userId}: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {canEditUserEntitlements} = useAdminAccessRight();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const {
        loadingError: accountRelationsLoadingError,
        result: accountEntitlements,
        cacheKeys: accountCacheKeys,
    } = useGetProductAccountEntitlements({accountId});

    const {
        loadingError: userRelationsLoadingError,
        result: userEntitlements,
        cacheKeys: userCacheKeys,
    } = useGetProductUserEntitlements({userId});

    const {
        disabled: isAccountRolesDisabled,
        isExpanded: isAccountRolesExpanded,
        toggleRolesExpansion: toggleAccountRolesExpansion,
        expandedRoleIds: expandedRoleIdsAccount,
        setExpandedRoleIds: setExpandedRoleIdsAccount,
    } = useExpandRoles({records: accountEntitlements?.data || []});

    const {
        disabled: isUserRolesDisabled,
        isExpanded: isUserRolesExpanded,
        toggleRolesExpansion: toggleUserRolesExpansion,
        expandedRoleIds: expandedRoleIdsUser,
        setExpandedRoleIds: setExpandedRoleIdsUser,
    } = useExpandRoles({records: userEntitlements?.data || []});

    const redirectToAssignResources = useCallback(() => {
        navigate(`/profile/user/${userId}/assign-product-resources`);
    }, [userId, navigate]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(MCP_USER_SELECTION_KEY));
            dispatch(mcpUserEntitlementsActions.clearData());
            dispatch(mcpUserEntitlementsStateActions.clearData());
            dispatch(mcpAccountEntitlementsActions.clearData());
            dispatch(mcpAccountEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(MCP_USER_SELECTION_KEY));
        dispatch(mcpUserEntitlementsActions.requireRefresh());
        dispatch(mcpAccountEntitlementsActions.requireRefresh());
    }, [dispatch]);

    const filterCustomResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(mcpUserEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const filterAccountResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(mcpAccountEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noUserResultMessage = useMemo(() => {
        const filteredResources = userCacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    No resources found for the selected criteria.
                </div>
            );
        }

        if (userEntitlements && !userEntitlements.data.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    {NO_ASSIGNED_USER_MCP_ENTITLEMENT_DESCRIPTION}
                </div>
            );
        }

        return null;
    }, [userCacheKeys.filters, userEntitlements]);

    const noAccountResultMessage = useMemo(() => {
        const filteredResources = accountCacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    No resources found for the selected criteria.
                </div>
            );
        }

        if (accountEntitlements && !accountEntitlements.data.length) {
            return (
                <div className={userEntitlementsNoDataLabel}>
                    {NO_ASSIGNED_ACCOUNT_MCP_ENTITLEMENT_DESCRIPTION}
                </div>
            );
        }

        return null;
    }, [accountCacheKeys.filters, accountEntitlements]);

    const canEdit = useMemo(
        () => canEditUserEntitlements(accountId),
        [canEditUserEntitlements, accountId],
    );

    return (
        <div className="user-entiltement-view">
            <StickyElement className={userEntitlementsCustomEntitlements}>
                <div className={`user-entitlements-header ${userEntitlementsHeader}`}>
                    <Group gap="m" justify="space-between" alignItems="center">
                        <Group gap="m" alignItems="center">
                            <div style={{width: 310}}>
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    <Group gap="m" alignItems="center">
                                        <Icon type={Icons.COGS}/>
                                        Custom User MCP Entitlements
                                    </Group>
                                </Typography>
                            </div>
                            <ProductResourceSuggest
                                onSearch={filterCustomResources}
                                userId={userId}
                                id="mcp-resource-suggest"
                            />
                        </Group>
                        <Group gap="m" alignItems="center">
                            <Label>Expand Roles</Label>
                            <StateButton
                                {...{
                                    name: 'expand-mcp-user-roles',
                                    disabled: isUserRolesDisabled,
                                    checked: isUserRolesExpanded,
                                    onChange: toggleUserRolesExpansion,
                                }}
                            />
                            {canEdit ? (
                                <Button variant="Type.PRIMARY" onClick={redirectToAssignResources}>
                                    Assign Resource(s)
                                </Button>
                            ) : null}
                        </Group>
                    </Group>
                </div>
            </StickyElement>
            <ServerResponseHandler serverError={userRelationsLoadingError} sectionName={USER_SECTION_NAME}>
                <BoxLoading loading={!userEntitlements}>
                    <div className={userEntitlementsLoadingContainer}>
                        {userEntitlements?.data.length ? (
                            <PreparedEntitlementsView
                                resourceFilter={
                                    (userCacheKeys.filters.resourceQuery as string | undefined) || ''
                                }
                                dataTableId="assign-mcp-user-entitlements"
                                rows={userEntitlements.data}
                                selectionKey={MCP_USER_SELECTION_KEY}
                                customOffset={136}
                                expandedRoleIds={expandedRoleIdsUser}
                                setExpandedRoleIds={setExpandedRoleIdsUser}
                                readonly={!canEdit}
                                toolRibbonClassName={userEntitlementsCustomEntitlementsTools}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={(
                                    <McpUserEntitlementsPagination
                                        totalCount={userEntitlements.totalCount}
                                    />
                                )}
                                shortHeader={<McpUserResourceNameHeader/>}>
                                {() => (
                                    <UnassignProductUserEntitlementsTool
                                        userId={userId}
                                        selectionKey={MCP_USER_SELECTION_KEY}
                                        onDone={refreshResults}
                                    />
                                )}
                            </PreparedEntitlementsView>
                        ) : noUserResultMessage}
                    </div>
                </BoxLoading>
            </ServerResponseHandler>
            <StickyElement className={userEntitlementsAccountEntitlements}>
                <div className={userEntitlementsHeader}>
                    <Group gap="m" justify="space-between" alignItems="center">
                        <Group gap="m" alignItems="center">
                            <div style={{width: 310}}>
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    <Group gap="m" alignItems="center">
                                        <Icon type={Icons.SITEMAP}/>
                                        Account MCP Entitlements
                                    </Group>
                                </Typography>
                            </div>
                            <ProductResourceSuggest
                                onSearch={filterAccountResources}
                                accountId={accountId}
                                id="mcp-account-resource-suggest"
                            />
                        </Group>
                        <Group gap="m" alignItems="center">
                            <Label>Expand Roles</Label>
                            <StateButton
                                {...{
                                    name: 'expand-mcp-account-roles',
                                    disabled: isAccountRolesDisabled,
                                    checked: isAccountRolesExpanded,
                                    onChange: toggleAccountRolesExpansion,
                                }}
                            />
                        </Group>
                    </Group>
                </div>
            </StickyElement>
            <ServerResponseHandler
                serverError={accountRelationsLoadingError}
                sectionName={ACCOUNT_SECTION_NAME}>
                <BoxLoading loading={!accountEntitlements}>
                    <div className={userEntitlementsLoadingContainer}>
                        {accountEntitlements?.data.length ? (
                            <PreparedEntitlementsView
                                dataTableId="assign-mcp-user-account-entitlements"
                                rows={accountEntitlements.data}
                                expandedRoleIds={expandedRoleIdsAccount}
                                setExpandedRoleIds={setExpandedRoleIdsAccount}
                                selectionKey={MCP_ACCOUNT_SELECTION_KEY}
                                customOffset={186}
                                readonly={true}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={(
                                    <McpAccountEntitlementsPagination
                                        totalCount={accountEntitlements.totalCount}
                                    />
                                )}
                                shortHeader={<McpAccountResourceNameHeader/>}
                                resourceFilter={
                                    (accountCacheKeys.filters.resourceQuery as
                                        string | undefined) || ''
                                }
                            />
                        ) : noAccountResultMessage}
                    </div>
                </BoxLoading>
            </ServerResponseHandler>
        </div>
    );
};
